import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { User } from './types/users';
import { ClanMember } from './types/clans';
import { CompletionTime } from './types/submissions';
import { createIsomorphicFn } from '@tanstack/react-start';
import { fetchPublicAssetFromServer } from './index.server';
import { z } from 'zod';
import { BaseLevel } from './types/levels';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

type PrimaryClickEvent = {
    defaultPrevented: boolean;
    button: number;
    metaKey: boolean;
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
};

export const primaryClickHandler =
    (callback?: (event: PrimaryClickEvent) => void) =>
    (event: PrimaryClickEvent) => {
        if (
            !event.defaultPrevented &&
            event.button === 0 &&
            !event.metaKey &&
            !event.altKey &&
            !event.ctrlKey &&
            !event.shiftKey
        )
            callback?.(event);
    };

export interface ModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const getAvatarUrl = (
    user: Partial<User> | Partial<ClanMember> | null,
) =>
    user && user?.discord_avatar
        ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.webp` +
          (user?.discord_avatar?.startsWith('a_') ? '?animated=true' : '')
        : '/assets/avatar_default.svg';

export const getAvatarDecorationUrl = (
    user: Partial<User> | Partial<ClanMember> | null,
) =>
    user && user?.discord_avatar_decoration
        ? `https://cdn.discordapp.com/avatar-decoration-presets/${user.discord_avatar_decoration}.png`
        : null;

export const toCamelCase = (str: string) =>
    str
        .split(/[\s_-]+/)
        .map(
            (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join('');

export const camelToSpaces = (str: string) =>
    str
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .trim()
        .replace(/^./, (s) => s.toUpperCase());

type VideoIdData = {
    provider: 'Youtube' | 'Medal' | 'Twitch' | 'Bilibili' | 'Unknown';
    id: string | null;
    start: string | null;
};

const unknownVideoIdData: VideoIdData = {
    provider: 'Unknown',
    id: null,
    start: null,
};

export const getVideoIdFromURL = (rawUrl?: string): VideoIdData => {
    if (!rawUrl) return unknownVideoIdData;
    try {
        const url = new URL(rawUrl);
        if (
            [
                'youtube.com',
                'youtu.be',
                'www.youtube.com',
                'm.youtube.com',
            ].includes(url.hostname)
        ) {
            const start =
                url.searchParams.get('t') ??
                url.searchParams.get('start') ??
                null;
            if (url.hostname === 'youtu.be') {
                return {
                    provider: 'Youtube',
                    id: url.pathname.slice(1),
                    start,
                };
            } else if (
                url.hostname === 'www.youtube.com' ||
                url.hostname === 'youtube.com' ||
                url.hostname === 'm.youtube.com'
            ) {
                return {
                    provider: 'Youtube',
                    id: url.searchParams.get('v'),
                    start,
                };
            }
        } else if (['medal.tv', 'www.medal.tv'].includes(url.hostname)) {
            const paths = url.pathname.split('/').filter(Boolean);
            const id = paths[paths.length - 1];
            return {
                provider: 'Medal',
                id,
                start: null,
            };
        } else if (
            [
                'twitch.tv',
                'www.twitch.tv',
                'm.twitch.tv',
                'clips.twitch.tv',
            ].includes(url.hostname)
        ) {
            const paths = url.pathname.split('/').filter(Boolean);
            const start = url.searchParams.get('t') ?? null;

            if (url.hostname === 'clips.twitch.tv' && paths[0]) {
                return {
                    provider: 'Twitch',
                    id: paths[0],
                    start,
                };
            }

            if ((paths[0] === 'videos' || paths[0] === 'clip') && paths[1]) {
                return {
                    provider: 'Twitch',
                    id: paths[1],
                    start,
                };
            }

            if (paths[1] === 'clip' && paths[2]) {
                return {
                    provider: 'Twitch',
                    id: paths[2],
                    start,
                };
            }
        } else if (
            [
                'bilibili.com',
                'www.bilibili.com',
                'm.bilibili.com',
                'b23.tv',
            ].includes(url.hostname)
        ) {
            const paths = url.pathname.split('/').filter(Boolean);

            if (url.hostname === 'b23.tv' && paths[0]) {
                return {
                    provider: 'Bilibili',
                    id: paths[0],
                    start: null,
                };
            }

            if (paths[0] === 'video' && paths[1]) {
                return {
                    provider: 'Bilibili',
                    id: paths[1],
                    start: null,
                };
            }
        }
    } catch (error) {
        console.warn('Failed to parse video URL:', error);
        return unknownVideoIdData;
    }
    return unknownVideoIdData;
};

export const toDatetimeLocal = (value: string) => {
    const date = new Date(value);
    return (
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0') +
        'T' +
        String(date.getHours()).padStart(2, '0') +
        ':' +
        String(date.getMinutes()).padStart(2, '0') +
        ':' +
        String(date.getSeconds()).padStart(2, '0')
    );
};

export function getFullLevelThumbnail(levelId: number) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/levels/full/${levelId}.webp`;
}

export function getCardLevelThumbnail(levelId: number) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/levels/cards/${levelId}.webp`;
}

export const getLevelDisplayName = (level: BaseLevel) =>
    `${getLevelDisplayPrefix(level)}${level.name}`;

export const getLevelDisplayPrefix = (level: BaseLevel) =>
    'status' in level
        ? level.status !== 'MainList'
            ? `${level.status} - `
            : level.position != null
              ? `#${level.position} `
              : ''
        : '';

export function getPackThumbnail(packid: string) {
    return `https://raw.githubusercontent.com/All-Rated-Extreme-Demon-List/Thumbnails/main/packs/${packid}.webp`;
}

export const completionTimeSplit = (time: number): CompletionTime => {
    const hours = Math.floor(time / 3600000);
    const minutes = Math.floor((time - hours * 3600000) / 60000);
    const seconds = Math.floor(
        (time - hours * 3600000 - minutes * 60000) / 1000,
    );
    const milliseconds = Math.floor(time % 1000);

    return {
        hours: hours,
        minutes: minutes,
        seconds: seconds,
        milliseconds: milliseconds,
    };
};

export const completionTimeToMs = (time: CompletionTime): number => {
    return (
        time.hours * 3600000 +
        time.minutes * 60000 +
        time.seconds * 1000 +
        time.milliseconds
    );
};
export const getCompletionTime = (time: number) => {
    const {
        hours: hours_raw,
        minutes: minutes_raw,
        seconds: seconds_raw,
        milliseconds: milliseconds_raw,
    } = completionTimeSplit(time);
    const hours = String(hours_raw).padStart(2, '0');
    const minutes = String(minutes_raw).padStart(2, '0');
    const seconds = String(seconds_raw).padStart(2, '0');
    const milliseconds = String(milliseconds_raw).padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const statusBgClass = (status: string) => {
    switch (status) {
        case 'Pending':
            return 'bg-pending/30 border-pending';
        case 'Accepted':
            return 'bg-confirm/30 border-confirm';
        case 'Denied':
            return 'bg-destructive/30 border-destructive';
        case 'Claimed':
            return 'bg-claimed/30 border-claimed';
        case 'UnderConsideration':
            return 'bg-claimed/30 border-claimed';
        default:
            return 'bg-pending/30 border-pending';
    }
};

export const trimStringToNull = (value: string | undefined | null) => {
    const trimmed = value?.trim();
    return !trimmed || trimmed === '' ? null : trimmed;
};

export const seo = ({
    title,
    description,
    image,
    imageAlt,
    video,
    fullImage = false,
    imagewidth = 1200,
    imageHeight = 630,
}: {
    title?: string;
    description?: string;
    image?: string;
    imageAlt?: string;
    video?: string;
    fullImage?: boolean;
    imagewidth?: number;
    imageHeight?: number;
}) => {
    return [
        { title: title || 'All Rated Extreme Demons List' },
        { name: 'description', content: description },
        {
            property: 'og:title',
            content: title || 'All Rated Extreme Demons List',
        },
        {
            property: 'twitter:title',
            content: title || 'All Rated Extreme Demons List',
        },
        { property: 'og:type', content: 'website' },
        {
            property: 'og:site_name',
            content: 'All Rated Extreme Demons List',
        },
        {
            property: 'og:image',
            content: image,
        },
        {
            property: 'twitter:image',
            content: image,
        },
        { property: 'og:image:alt', content: imageAlt || 'AREDL' },
        {
            property: 'og:image:width',
            content: image ? String(imagewidth) : undefined,
        },
        {
            property: 'og:image:height',
            content: image ? String(imageHeight) : undefined,
        },

        {
            property: 'twitter:card',
            content: video
                ? 'player'
                : fullImage
                  ? 'summary_large_image'
                  : 'summary',
        },
        {
            property: 'og:description',
            content: description,
        },
        {
            property: 'twitter:description',
            content: description,
        },
        {
            property: 'og:url',
            content: 'https://aredl.net/',
        },
        {
            property: 'twitter:url',
            content: 'https://aredl.net/',
        },
        {
            property: 'og:video',
            content: video,
        },
        {
            property: 'twitter:player:width',
            content: video ? '1200' : undefined,
        },
        {
            property: 'twitter:player:height',
            content: video ? '630' : undefined,
        },
        { property: 'twitter:player', content: video },
        { property: 'theme-color', content: '#FF6F00' },
    ];
};

export const fetchPublicAsset = createIsomorphicFn()
    .server(fetchPublicAssetFromServer)
    .client(async (path: string) => await fetch(`${path}`));

export const includeDifferentProperties = <
    T1 extends Record<string, unknown>,
    T2 extends Record<string, unknown>,
>(
    first: Partial<T1>,
    second: Partial<T2>,
): Record<string, unknown> => {
    return Object.keys(first).reduce(
        (acc, key) => {
            if (
                (key in first && !(key in second)) ||
                first[key] !== second[key]
            ) {
                acc[key] = first[key];
            }
            return acc;
        },
        {} as Record<string, unknown>,
    );
};

type ZodShape = Record<string, z.ZodTypeAny>;

type NullableDefaultObjectShape<TShape extends ZodShape> = {
    [key in keyof TShape]: z.ZodDefault<z.ZodNullable<TShape[key]>>;
};

export function nullableDefaultObject<TShape extends ZodShape>(
    schema: z.ZodObject<TShape>,
) {
    const newProps = {} as NullableDefaultObjectShape<TShape>;

    for (const key in schema.shape) {
        const value = schema.shape[key];
        newProps[key] = value.nullable().default(null);
    }

    return z.object(newProps);
}

export const formatLevelGDID = (level: BaseLevel) =>
    `${level.level_id}${level.two_player ? '_2p' : ''}`;

export type DebouncedFunction<TArgs extends unknown[]> = ((
    ...args: TArgs
) => void) & {
    cancel: () => void;
};

export function debounce<TArgs extends unknown[]>(
    callback: (...args: TArgs) => void,
    wait: number,
): DebouncedFunction<TArgs> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const debounced = ((...args: TArgs) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            timeoutId = undefined;
            callback(...args);
        }, wait);
    }) as DebouncedFunction<TArgs>;

    debounced.cancel = () => {
        if (!timeoutId) return;
        clearTimeout(timeoutId);
        timeoutId = undefined;
    };

    return debounced;
}
